/**
 * Goal Completion Chart Modal - Coach Dashboard Goal Performance Visualization
 *
 * Displays client goal completion percentage over time using weekly rollup data
 * Shows trend of goal performance week-over-week
 *
 * Uses the modular MetricChart system
 */

import React, { useState, useEffect } from 'react';
import { X, Target, Calendar } from 'lucide-react';
import { MetricChart, MetricChartConfig, ChartDataPoint } from '../shared/charts';

interface GoalCompletionDataPoint {
  date: string; // Monday of the week (YYYY-MM-DD)
  week_key: string; // e.g., "2025-W41"
  completion_percentage: number; // 0-100
}

interface GoalCompletionChartModalProps {
  clientId: string;
  clientName: string;
  isOpen: boolean;
  onClose: () => void;
}

type DateRange = '30' | '90' | '180' | '365' | 'all' | 'custom';

export const GoalCompletionChartModal: React.FC<GoalCompletionChartModalProps> = ({
  clientId,
  clientName,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [goalCompletionData, setGoalCompletionData] = useState<GoalCompletionDataPoint[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>('90');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchGoalCompletionData();
    }
  }, [isOpen, dateRange, customStartDate, customEndDate]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchGoalCompletionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build endpoint URL
      let url = `/api/coach/clients/${clientId}/goal-completion-history`;

      // Add date range parameters
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url += `?startDate=${customStartDate}&endDate=${customEndDate}`;
      } else if (dateRange !== 'all') {
        url += `?days=${dateRange}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setGoalCompletionData(data.history || []);
      } else {
        throw new Error('Failed to fetch goal completion history');
      }
    } catch (err) {
      console.error('Error fetching goal completion history:', err);
      setError('Failed to load goal completion data');
    } finally {
      setLoading(false);
    }
  };

  // Transform goal completion data to chart format
  const transformDataToChartData = (): ChartDataPoint[] => {
    return goalCompletionData.map((point) => {
      const label = point.week_key;

      // Determine if week met "good" threshold (>=70%)
      const metTarget = point.completion_percentage >= 70;

      return {
        label,
        value: point.completion_percentage,
        metTarget,
        metadata: {
          date: point.date,
          week_key: point.week_key
        }
      };
    });
  };

  // Calculate statistics
  const calculateStats = () => {
    if (goalCompletionData.length === 0) return null;

    const percentages = goalCompletionData.map(d => d.completion_percentage);
    const startPercentage = percentages[0];
    const currentPercentage = percentages[percentages.length - 1];
    const totalChange = currentPercentage - startPercentage;
    const avgPercentage = percentages.reduce((a, b) => a + b, 0) / percentages.length;
    const maxPercentage = Math.max(...percentages);
    const minPercentage = Math.min(...percentages);

    // Count weeks above thresholds
    const weeksAbove70 = percentages.filter(p => p >= 70).length;
    const weeksAbove80 = percentages.filter(p => p >= 80).length;

    return {
      startPercentage,
      currentPercentage,
      totalChange,
      avgPercentage,
      maxPercentage,
      minPercentage,
      weeksAbove70,
      weeksAbove80,
      totalWeeks: percentages.length
    };
  };

  // Build chart configuration using MetricChartConfig
  const buildChartConfig = (): MetricChartConfig => {
    return {
      type: 'line',
      datasets: [
        {
          label: 'Weekly Goal Completion',
          data: transformDataToChartData(),
          color: '#10b981', // Green
          showPoints: true,
          tension: 0.3,
        }
      ],
      gapDetection: 'weekly',
      xAxisLabel: 'Week',
      yAxisLabel: 'Goal Completion (%)',
      yAxisBeginAtZero: true,
      yAxisMax: 100, // Percentage scale
      showLegend: false,
      showTooltips: true,
      customTooltip: (dataPoint: ChartDataPoint) => {
        const value = dataPoint.value !== null ? `${dataPoint.value.toFixed(1)}%` : 'No data';
        const date = dataPoint.metadata?.date || dataPoint.label;

        const lines = [
          `Week: ${dataPoint.metadata?.week_key || dataPoint.label}`,
          `Completion: ${value}`,
          `Start Date: ${new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        ];

        // Add performance indicator
        if (dataPoint.value !== null) {
          if (dataPoint.value >= 80) {
            lines.push('🟢 Excelling');
          } else if (dataPoint.value >= 70) {
            lines.push('🔵 Strong Performance');
          } else if (dataPoint.value >= 50) {
            lines.push('🟡 Moderate');
          } else {
            lines.push('🟠 Needs Attention');
          }
        }

        return lines;
      },
      loading,
      error,
      emptyMessage: 'No goal completion data available for this date range',
      height: 400,
      responsive: true,
      maintainAspectRatio: false
    };
  };

  const stats = calculateStats();

  if (!isOpen) return null;

  return (
    <div className="goal-completion-modal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            <Target className="w-6 h-6" />
            Weekly Goal Completion - {clientName}
          </h2>
          <button onClick={onClose} className="close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          {/* Date Range Selector */}
          <div className="date-range-selector">
            <div className="range-buttons">
              <button
                className={`range-btn ${dateRange === '30' ? 'active' : ''}`}
                onClick={() => setDateRange('30')}
              >
                Last 30 Days
              </button>
              <button
                className={`range-btn ${dateRange === '90' ? 'active' : ''}`}
                onClick={() => setDateRange('90')}
              >
                Last 90 Days
              </button>
              <button
                className={`range-btn ${dateRange === '180' ? 'active' : ''}`}
                onClick={() => setDateRange('180')}
              >
                Last 6 Months
              </button>
              <button
                className={`range-btn ${dateRange === '365' ? 'active' : ''}`}
                onClick={() => setDateRange('365')}
              >
                Last 12 Months
              </button>
              <button
                className={`range-btn ${dateRange === 'all' ? 'active' : ''}`}
                onClick={() => setDateRange('all')}
              >
                All Time
              </button>
              <button
                className={`range-btn ${dateRange === 'custom' ? 'active' : ''}`}
                onClick={() => setDateRange('custom')}
              >
                <Calendar className="w-4 h-4" />
                Custom Range
              </button>
            </div>

            {dateRange === 'custom' && (
              <div className="custom-date-inputs">
                <div className="date-input-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="date-input-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Chart Area - Using MetricChart! */}
          <div className="chart-area">
            <MetricChart config={buildChartConfig()} />
          </div>

          {/* Statistics Footer */}
          {stats && (
            <div className="stats-footer">
              <div className="stat-item">
                <span className="label">Starting %</span>
                <span className="value">{stats.startPercentage.toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="label">Current %</span>
                <span className="value">{stats.currentPercentage.toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="label">Change</span>
                <span className={`value ${stats.totalChange > 0 ? 'positive' : stats.totalChange < 0 ? 'negative' : ''}`}>
                  {stats.totalChange > 0 ? '+' : ''}{stats.totalChange.toFixed(1)}%
                </span>
              </div>
              <div className="stat-item">
                <span className="label">Average</span>
                <span className="value">{stats.avgPercentage.toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="label">Range</span>
                <span className="value">
                  {stats.minPercentage.toFixed(1)}% - {stats.maxPercentage.toFixed(1)}%
                </span>
              </div>
              <div className="stat-item">
                <span className="label">Weeks ≥70%</span>
                <span className="value">{stats.weeksAbove70}/{stats.totalWeeks}</span>
              </div>
              <div className="stat-item">
                <span className="label">Weeks ≥80%</span>
                <span className="value">{stats.weeksAbove80}/{stats.totalWeeks}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .goal-completion-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    z-index: 1;
  }

  .modal-content {
    position: relative;
    width: 95vw;
    max-width: 1200px;
    max-height: 90vh;
    background: var(--bg-primary);
    border-radius: var(--radius-large);
    border: 1px solid var(--border-primary);
    box-shadow: var(--shadow-heavy);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid var(--border-primary);
    background: var(--bg-primary);
  }

  .modal-header h2 {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .close-btn {
    padding: 8px;
    background: none;
    border: none;
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .date-range-selector {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .range-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .range-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-medium);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .range-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }

  .range-btn.active {
    background: var(--accent-blue);
    color: white;
    border-color: var(--accent-blue);
  }

  .custom-date-inputs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .date-input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .date-input-group label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .date-input-group input {
    padding: 10px 12px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-medium);
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .date-input-group input:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .chart-area {
    flex: 1;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stats-footer {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
    padding: 20px;
    background: var(--bg-secondary);
    border-radius: var(--radius-medium);
    border: 1px solid var(--border-primary);
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .stat-item .label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-item .value {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .stat-item .value.positive {
    color: var(--success);
  }

  .stat-item .value.negative {
    color: var(--danger);
  }

  @media (max-width: 768px) {
    .modal-content {
      width: 98vw;
      max-height: 95vh;
    }

    .modal-header,
    .modal-body {
      padding: 16px;
    }

    .range-buttons {
      flex-direction: column;
    }

    .stats-footer {
      grid-template-columns: 1fr;
    }
  }
`;
